<!--
  @component Styles — Reactive Slate Ink theme wrapper.
  @module @liquidefy/lyn/global/visuals/styles/Styles
  @license @lqdresearch

  @author Scott Martin
  @since 1.0.0
  @tier Infrastructure

  ─────────────────────────────────────────────────────────────────
  ARCHITECTURE ROLE: STYLING SYSTEM
  ─────────────────────────────────────────────────────────────────

    Styles.svelte is the reactive wrapper for the Slate Ink design
    system. It holds user mode preference, watches OS preferences,
    resolves the active mode, binds recipes to current tokens, writes
    the data-theme attribute to <html>, and exposes everything via
    useStyles() context.

    This file absorbs what used to live in three separate files in
    the Lyn architecture:
      - styles-io.ts  (recipe composition)        → now in styles.ts
      - StylesIO.svelte (reactive bridge)         → THIS FILE
      - Modes.svelte (user preference + OS watch) → THIS FILE

    The chain:
      styles.ts (pure engine)
        ↓ imports
      Styles.svelte (this file — reactive bridge)
        ↓ setContext(STYLES_CONTEXT_KEY)
      useStyles() → { mode, tokens, recipes, setUserMode, toggleMode }

  ─────────────────────────────────────────────────────────────────
  WHY SELF-CONTAINED (NO DEVICE DEPENDENCY)
  ─────────────────────────────────────────────────────────────────

    Styles.svelte has its own matchMedia listeners for
    prefers-color-scheme and prefers-contrast rather than reading
    from Device context. This is intentional:

      1. Styles mounts OUTSIDE Global in App.svelte's tree.
         Device mounts INSIDE Global. Context flow doesn't reach
         upward, so Styles cannot call useDevice().

      2. Self-containment means Styles can be tested, mounted, or
         SSR'd without any other part of the global system.

      3. matchMedia listeners are essentially free — one OS event
         per preference change, which happens rarely. The small
         overlap with Device.svelte (which also watches these) is
         acceptable for the architectural cleanliness.

  ─────────────────────────────────────────────────────────────────
  MODE RESOLUTION
  ─────────────────────────────────────────────────────────────────

    Priority order:
      1. User preference (if not "system")  → use as-is
      2. System prefers-contrast: more      → "high-contrast"
      3. System prefers-color-scheme: dark  → "dark"
      4. Default                            → "light"

    High-contrast is checked before dark because when a user has
    enabled OS high-contrast, they've made a stronger statement
    about their needs than simple dark-mode preference.

  ─────────────────────────────────────────────────────────────────
  PERSISTENCE
  ─────────────────────────────────────────────────────────────────

    User preference is stored at localStorage key "network:mode".
    Values: "dark" | "light" | "high-contrast" | "system"
    "system" clears the key (means "follow OS").
    Storage failures are silently ignored — preference still applies
    in-memory for the session.

  Props:
    initialPreference?: UserModePreference  — default "system"
    children:           Snippet

  Context provided:
    Key:  STYLES_CONTEXT_KEY  ("styles")
    Type: StylesContext
    Hook: useStyles()

  @example
  <script lang="ts">
    import { useStyles } from '@liquidefy/lyn/global/visuals/styles/Styles.svelte';
    const styles = useStyles();
  </script>

  <div style={styleString(styles.recipes.glass.frost())}>
    <button onclick={() => styles.toggleMode()}>
      Switch to {styles.isDark ? 'light' : 'dark'}
    </button>
  </div>
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import {
    type ThemeMode,
    type UserModePreference,
    type ThemeTokens,
    type BoundRecipes,
  } from "./styles.js";

  /** Svelte context key for the styles system. */
  export const STYLES_CONTEXT_KEY = "styles" as const;

  /** localStorage key for persisted user mode preference. */
  export const STYLES_STORAGE_KEY = "network:mode";

  /**
   * Styles context exposed via useStyles().
   * All reactive properties are getters reading $state/$derived at
   * access time. Svelte tracks each read individually — a component
   * reading `tokens.accent.solid` does not re-render when `mode`
   * changes unless `mode` actually affected `accent.solid`.
   */
  export interface StylesContext {
    /** The fully resolved visual mode currently active. */
    readonly mode: ThemeMode;
    /** The user's explicit preference ("system" if they haven't pinned one). */
    readonly userPreference: UserModePreference;
    /** Active token set for the current mode. */
    readonly tokens: ThemeTokens;
    /** All recipes pre-bound to current tokens — zero-arg calls. */
    readonly recipes: BoundRecipes;

    // ── Predicates ────────────────────────────────────────────
    readonly isDark:          boolean;
    readonly isLight:         boolean;
    readonly isHighContrast:  boolean;
    /** True when the user has an explicit preference (not "system"). */
    readonly isOverridden:    boolean;

    // ── Actions ───────────────────────────────────────────────
    /**
     * Set the user's mode preference.
     * "system" clears any override. Persists to localStorage.
     */
    setUserMode(pref: UserModePreference): void;

    /**
     * Toggle between dark and light.
     * If currently high-contrast, toggles to dark.
     */
    toggleMode(): void;

    /** Reset to system preference — clears any override. */
    resetToSystem(): void;
  }

  /**
   * Access the styles context from any component mounted below
   * Styles in the component tree.
   *
   * @throws If called outside a Styles component tree.
   */
  export function useStyles(): StylesContext {
    const ctx = getContext<StylesContext | undefined>(STYLES_CONTEXT_KEY);
    if (!ctx) {
      throw new Error(
        "useStyles() called outside of a Styles component tree. " +
        "Ensure Styles.svelte is mounted at the top of App.svelte's tree.",
      );
    }
    return ctx;
  }

  // Re-export common types for consumers importing from here.
  export type {
    ThemeMode,
    UserModePreference,
    ThemeTokens,
    BoundRecipes,
  } from "./styles.js";
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  // `ThemeMode` and `UserModePreference` are imported in <script module>
  // above; svelte-check (4.x) merges the two script blocks for
  // type-identifier scope and flags a re-import here as duplicate.
  // Network-copy patch #2 (post-duplication, alongside the
  // "network:mode" storage key change).
  import { getTokens, bindRecipes } from "./styles.js";

  // ══════════════════════════════════════════════════════════════
  // PROPS
  // ══════════════════════════════════════════════════════════════

  interface Props {
    /**
     * Initial preference before localStorage is consulted.
     * Default "system" — follow OS preference.
     */
    initialPreference?: UserModePreference;
    children: Snippet;
  }

  let {
    initialPreference = "system",
    children,
  }: Props = $props();

  // ══════════════════════════════════════════════════════════════
  // LOCALSTORAGE
  //
  // Read stored preference on mount, write on change. Storage
  // failures (private browsing, quota, no storage) are silently
  // ignored — preference still applies in memory.
  // ══════════════════════════════════════════════════════════════

  function readStoredPreference(): UserModePreference | null {
    if (typeof localStorage === "undefined") return null;
    try {
      const stored = localStorage.getItem(STYLES_STORAGE_KEY);
      if (
        stored === "dark" ||
        stored === "light" ||
        stored === "high-contrast" ||
        stored === "system"
      ) {
        return stored;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  function writeStoredPreference(pref: UserModePreference): void {
    if (typeof localStorage === "undefined") return;
    try {
      if (pref === "system") {
        localStorage.removeItem(STYLES_STORAGE_KEY);
      } else {
        localStorage.setItem(STYLES_STORAGE_KEY, pref);
      }
    } catch {
      /* ignore */
    }
  }

  // ══════════════════════════════════════════════════════════════
  // MATCHMEDIA HELPERS
  //
  // Self-contained OS preference watching. Styles.svelte does not
  // depend on Device.svelte because Styles mounts outside the Global
  // tree where Device lives.
  // ══════════════════════════════════════════════════════════════

  const DARK_MQ = "(prefers-color-scheme: dark)";
  const HC_MQ   = "(prefers-contrast: more)";

  function mqMatches(query: string): boolean {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  }

  function watchMq(
    query:  string,
    setter: (value: boolean) => void,
  ): (() => void) | null {
    if (typeof window === "undefined") return null;
    try {
      const mql     = window.matchMedia(query);
      const handler = (e: MediaQueryListEvent) => setter(e.matches);
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } catch {
      return null;
    }
  }

  // ══════════════════════════════════════════════════════════════
  // STATE
  //
  // userPreference:        explicit user choice (or "system")
  // systemPrefersDark:     OS prefers-color-scheme: dark
  // systemPrefersContrast: OS prefers-contrast: more
  //
  // All Cold tier — change on discrete user actions (toggle) or
  // OS-level preference changes (rare).
  // ══════════════════════════════════════════════════════════════

  let userPreference         = $state<UserModePreference>(initialPreference);
  let systemPrefersDark      = $state(mqMatches(DARK_MQ));
  let systemPrefersContrast  = $state(mqMatches(HC_MQ));

  // ══════════════════════════════════════════════════════════════
  // INITIAL LOCALSTORAGE READ
  //
  // $effect fires after mount, at which point localStorage is safe
  // to read. If a stored preference exists, it overrides the
  // initial prop default.
  // ══════════════════════════════════════════════════════════════

  $effect(() => {
    const stored = readStoredPreference();
    if (stored !== null) userPreference = stored;
  });

  // ══════════════════════════════════════════════════════════════
  // MATCHMEDIA WATCHERS
  //
  // One listener per OS preference. Cleans up on unmount.
  // ══════════════════════════════════════════════════════════════

  $effect(() => {
    const cleanups: Array<(() => void) | null> = [
      watchMq(DARK_MQ, v => { systemPrefersDark     = v; }),
      watchMq(HC_MQ,   v => { systemPrefersContrast = v; }),
    ];
    return () => cleanups.forEach(c => c?.());
  });

  // ══════════════════════════════════════════════════════════════
  // RESOLUTION — $DERIVED (JUSTIFIED)
  //
  // resolvedMode is the key for token selection and recipe binding.
  // $derived caches the resolution and recomputes only when its
  // dependencies change (userPreference or system prefs).
  //
  // activeTokens and activeRecipes follow the same pattern —
  // $derived because they gate downstream reactivity and constructing
  // them eagerly would be wasteful on every read.
  // ══════════════════════════════════════════════════════════════

  function resolveMode(
    pref:        UserModePreference,
    systemDark:  boolean,
    systemHC:    boolean,
  ): ThemeMode {
    if (pref === "dark")          return "dark";
    if (pref === "light")         return "light";
    if (pref === "high-contrast") return "high-contrast";
    // "system":
    if (systemHC)                 return "high-contrast";
    return systemDark ? "dark" : "light";
  }

  let resolvedMode  = $derived(resolveMode(userPreference, systemPrefersDark, systemPrefersContrast));
  let activeTokens  = $derived(getTokens(resolvedMode));
  let activeRecipes = $derived(bindRecipes(activeTokens));

  // ══════════════════════════════════════════════════════════════
  // DATA-THEME ATTRIBUTE
  //
  // Writes data-theme to <html> so CSS selectors in <svelte:head>
  // can respond (::selection, body background, etc.) and any
  // component-level CSS using [data-theme="..."] selectors works.
  // ══════════════════════════════════════════════════════════════

  $effect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", resolvedMode);
  });

  // ══════════════════════════════════════════════════════════════
  // ACTIONS
  // ══════════════════════════════════════════════════════════════

  function setUserMode(pref: UserModePreference): void {
    userPreference = pref;
    writeStoredPreference(pref);
  }

  function toggleMode(): void {
    // Dark <-> Light toggle. HC escapes to dark.
    const current = resolvedMode;
    const next: ThemeMode = current === "dark" ? "light" : "dark";
    setUserMode(next);
  }

  function resetToSystem(): void {
    setUserMode("system");
  }

  // ══════════════════════════════════════════════════════════════
  // CONTEXT
  // ══════════════════════════════════════════════════════════════

  setContext<StylesContext>(STYLES_CONTEXT_KEY, {
    get mode()           { return resolvedMode; },
    get userPreference() { return userPreference; },
    get tokens()         { return activeTokens; },
    get recipes()        { return activeRecipes; },

    get isDark()         { return resolvedMode === "dark"; },
    get isLight()        { return resolvedMode === "light"; },
    get isHighContrast() { return resolvedMode === "high-contrast"; },
    get isOverridden()   { return userPreference !== "system"; },

    setUserMode,
    toggleMode,
    resetToSystem,
  });
</script>

<svelte:head>
  <!--
    CSS reset + Slate Ink base rules + mode-keyed CSS custom properties.

    Three layers of CSS:
      1. Universal reset — normalizes browser defaults, no mode awareness
      2. Universal CSS vars — mode-independent tokens (spacing, radii,
         typography, easing, layout dimensions)
      3. Mode-specific CSS vars — selected via [data-theme="..."]

    Components get two styling paths:
      - Recipes (via useStyles().recipes) for inline styles and Svelte
        class bindings — the primary API
      - CSS custom properties for plain CSS in <style> blocks and
        pseudo-element rules (::selection, ::placeholder, etc.)

    CSS var naming matches the Slate Ink design prototype so existing
    prototype CSS transplants cleanly.
  -->
  <style>
    /* ══════════════════════════════════════════════════════════════
       1. UNIVERSAL CSS RESET
       ══════════════════════════════════════════════════════════════ */

    *, *::before, *::after {
      box-sizing:  border-box;
      margin:      0;
      padding:     0;
      border:      0;
    }

    html {
      -webkit-text-size-adjust: 100%;
      text-size-adjust:         100%;
      scroll-behavior:          smooth;
      -webkit-font-smoothing:   antialiased;
      -moz-osx-font-smoothing:  grayscale;
      text-rendering:           optimizeLegibility;
      tab-size:                 2;
    }

    html, body {
      height:   100dvh;
      overflow: hidden;
    }

    body {
      font-family:    var(--type-font);
      font-size:      15px;
      line-height:    var(--type-leading);
      letter-spacing: var(--type-tracking);
      min-height:     100dvh;
      transition:     color 400ms ease, background 400ms ease;
    }

    img, picture, video, canvas, svg {
      display:   block;
      max-width: 100%;
    }

    input, button, textarea, select {
      font:       inherit;
      color:      inherit;
      background: transparent;
    }

    button { cursor: pointer; }
    button:disabled { cursor: not-allowed; }

    p, h1, h2, h3, h4, h5, h6 { overflow-wrap: break-word; }

    ul[role="list"], ol[role="list"] { list-style: none; }

    #app, #root { isolation: isolate; }

    :focus-visible {
      outline:        2px solid var(--border-focus);
      outline-offset: 2px;
    }
    :focus:not(:focus-visible) { outline: none; }

    /* ══════════════════════════════════════════════════════════════
       2. UNIVERSAL CSS VARS (mode-independent)
       ══════════════════════════════════════════════════════════════ */

    :root {
      /* Spacing */
      --space-1: 4px;
      --space-2: 8px;
      --space-3: 12px;
      --space-4: 16px;
      --space-5: 20px;
      --space-6: 24px;
      --space-8: 32px;

      /* Radii */
      --radius-xs:   4px;
      --radius-sm:   6px;
      --radius-md:   8px;
      --radius-lg:   12px;
      --radius-xl:   16px;
      --radius-pill: 999px;

      /* Layout */
      --header-h:     56px;
      --sidebar-w:    280px;
      --sidebar-rail: 48px;
      --input-h:      72px;
      --reading-w:    720px;

      /* Typography */
      --type-font:     'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      --type-mono:     'SF Mono', 'Fira Code', monospace;
      --type-tracking: -0.012em;
      --type-leading:  1.6;

      /* Motion */
      --ease-fast:   150ms ease;
      --ease-normal: 250ms ease;
      --ease-smooth: 400ms cubic-bezier(0.2, 0.8, 0.2, 1);
      --ease-spring: 500ms cubic-bezier(0.2, 0.8, 0.2, 1);
    }

    /* ══════════════════════════════════════════════════════════════
       3. MODE-SPECIFIC CSS VARS + body background
       Values match DARK/LIGHT/HIGH_CONTRAST in styles.ts exactly.
       ══════════════════════════════════════════════════════════════ */

    /* ── DARK ─────────────────────────────────────────────────── */
    [data-theme="dark"] {
      --surface-base:     #15151b;
      --surface-raised:   #1a1a21;
      --surface-sunken:   #111116;
      --surface-overlay:  rgba(21,21,27,0.85);

      --glass-fill:       rgba(255,255,255,0.04);
      --glass-fill-hover: rgba(255,255,255,0.06);
      --glass-border:     0.5px solid rgba(255,255,255,0.08);
      --glass-blur:       blur(24px);

      --text-primary:     rgba(255,255,255,0.92);
      --text-secondary:   rgba(255,255,255,0.44);
      --text-tertiary:    rgba(255,255,255,0.25);
      --text-ghost:       rgba(255,255,255,0.15);
      --text-placeholder: rgba(255,255,255,0.28);

      --border:           rgba(255,255,255,0.08);
      --border-subtle:    rgba(255,255,255,0.05);
      --border-strong:    rgba(255,255,255,0.12);
      --border-input:     rgba(255,255,255,0.10);
      --border-focus:     rgba(255,255,255,0.22);

      --bg-hover:         rgba(255,255,255,0.04);
      --bg-active:        rgba(255,255,255,0.07);
      --bg-selected:      rgba(255,255,255,0.06);

      --icon:             rgba(255,255,255,0.38);
      --icon-hover:       rgba(255,255,255,0.88);

      --accent:           #60a5fa;
      --accent-subtle:    rgba(96,165,250,0.12);
      --accent-text:      rgba(96,165,250,0.90);
      --accent-border:    rgba(96,165,250,0.20);
      --accent-glow:      rgba(96,165,250,0.08);

      --success:          #34d399;
      --success-subtle:   rgba(52,211,153,0.10);
      --warning:          #fbbf24;
      --warning-subtle:   rgba(251,191,36,0.10);
      --error:            #f87171;
      --error-subtle:     rgba(248,113,113,0.10);

      --shadow-sm:        0 1px 4px rgba(0,0,0,0.15);
      --shadow-md:        0 2px 8px rgba(0,0,0,0.20);
      --shadow-lg:        0 4px 16px rgba(0,0,0,0.25);
      --shadow-header:    0 1px 6px rgba(0,0,0,0.20);
    }
    [data-theme="dark"] body {
      color:      var(--text-primary);
      background: var(--surface-base);
    }
    [data-theme="dark"] ::selection {
      background: rgba(255,255,255,0.15);
      color:      inherit;
    }

    /* ── LIGHT ────────────────────────────────────────────────── */
    [data-theme="light"] {
      --surface-base:     #f2f2f5;
      --surface-raised:   #ffffff;
      --surface-sunken:   #ededf0;
      --surface-overlay:  rgba(242,242,245,0.85);

      --glass-fill:       linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(248,248,252,0.75) 100%);
      --glass-fill-hover: linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,248,252,0.88) 100%);
      --glass-border:     0.5px solid rgba(0,0,0,0.06);
      --glass-blur:       blur(24px);

      --text-primary:     rgba(0,0,0,0.88);
      --text-secondary:   rgba(0,0,0,0.44);
      --text-tertiary:    rgba(0,0,0,0.22);
      --text-ghost:       rgba(0,0,0,0.12);
      --text-placeholder: rgba(0,0,0,0.30);

      --border:           rgba(0,0,0,0.08);
      --border-subtle:    rgba(0,0,0,0.04);
      --border-strong:    rgba(0,0,0,0.12);
      --border-input:     rgba(0,0,0,0.10);
      --border-focus:     rgba(0,0,0,0.24);

      --bg-hover:         rgba(0,0,0,0.03);
      --bg-active:        rgba(0,0,0,0.06);
      --bg-selected:      rgba(0,0,0,0.05);

      --icon:             rgba(0,0,0,0.36);
      --icon-hover:       rgba(0,0,0,0.84);

      --accent:           #3a6ef5;
      --accent-subtle:    rgba(58,110,245,0.10);
      --accent-text:      rgba(58,110,245,0.90);
      --accent-border:    rgba(58,110,245,0.20);
      --accent-glow:      rgba(58,110,245,0.06);

      --success:          #059669;
      --success-subtle:   rgba(5,150,105,0.08);
      --warning:          #d97706;
      --warning-subtle:   rgba(217,119,6,0.08);
      --error:            #dc2626;
      --error-subtle:     rgba(220,38,38,0.08);

      --shadow-sm:        0 1px 4px rgba(0,0,0,0.05);
      --shadow-md:        0 2px 8px rgba(0,0,0,0.06);
      --shadow-lg:        0 4px 16px rgba(0,0,0,0.08);
      --shadow-header:    0 1px 6px rgba(0,0,0,0.05);
    }
    [data-theme="light"] body {
      color:      var(--text-primary);
      background: var(--surface-base);
    }
    [data-theme="light"] ::selection {
      background: rgba(0,0,0,0.12);
      color:      inherit;
    }

    /* ── HIGH-CONTRAST ────────────────────────────────────────── */
    [data-theme="high-contrast"] {
      --surface-base:     #000000;
      --surface-raised:   #000000;
      --surface-sunken:   #000000;
      --surface-overlay:  rgba(0,0,0,0.95);

      --glass-fill:       #000000;
      --glass-fill-hover: #1a1a1a;
      --glass-border:     1px solid #ffffff;
      --glass-blur:       blur(0px);

      --text-primary:     #ffffff;
      --text-secondary:   #ffffff;
      --text-tertiary:    #ffff00;
      --text-ghost:       #ffff00;
      --text-placeholder: #ffff00;

      --border:           #ffffff;
      --border-subtle:    #ffffff;
      --border-strong:    #ffffff;
      --border-input:     #ffffff;
      --border-focus:     #ffff00;

      --bg-hover:         #1a1a1a;
      --bg-active:        #333333;
      --bg-selected:      #0066cc;

      --icon:             #ffffff;
      --icon-hover:       #ffff00;

      --accent:           #4d9fff;
      --accent-subtle:    #001a33;
      --accent-text:      #4d9fff;
      --accent-border:    #4d9fff;
      --accent-glow:      transparent;

      --success:          #00ff00;
      --success-subtle:   #002200;
      --warning:          #ffaa00;
      --warning-subtle:   #221100;
      --error:            #ff3333;
      --error-subtle:     #220000;

      --shadow-sm:        none;
      --shadow-md:        none;
      --shadow-lg:        none;
      --shadow-header:    none;
    }
    [data-theme="high-contrast"] body {
      color:      var(--text-primary);
      background: var(--surface-base);
    }
    [data-theme="high-contrast"] ::selection {
      background: #ffff00;
      color:      #000000;
    }

    /* ══════════════════════════════════════════════════════════════
       4. GLOBAL ANIMATIONS (used by recipes)
       ══════════════════════════════════════════════════════════════ */

    @keyframes skeleton-shimmer {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }

    @keyframes av-shimmer {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  </style>
</svelte:head>

{@render children()}
